import { ComposerAreaNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerTemplateNodeAdapter } from './composerNodesAdapter';

// Square capture resolution in CSS px (final pixels = size * devicePixelRatio). Matches the classic customizer preview size.
const PREVIEW_CANVAS_SIZE = 512;
// setSide waits for raster images to load but never rejects on a broken/404 URL, so cap the wait with a timeout.
const SET_SIDE_TIMEOUT_MS = 15000;

/**
 * Renders the first non-empty side of a predesign template design to a 2D canvas
 * (the same paper.js customizer canvas the merchant edits) and returns a PNG data URL
 * suitable for `design.tempPreviewImageData`, or null when the design has no items on any side.
 *
 * This mirrors what {@link ComposerDesignEditor} renders (MPlaza.Customizer with
 * showSideImage=false), but in viewer mode so no editing chrome (dashed edges/overlays)
 * is captured. The preview is intentionally 2D only and independent of the 3D
 * configuration — it is just the design canvas of the first non-empty side.
 */
export const generateDesignPreviewDataUrl = async (design: MPlaza.Design, areas: ComposerAreaNode[], model: MPlaza.Model): Promise<string | null> => {
    // First side (in product side order) that actually has design items.
    let firstNonEmptySide: MPlaza.Side | null = null;
    for (const area of areas) {
        const side = ComposerTemplateNodeAdapter.fromAreaNodeToSide(area);
        if (design.getSideDesignItems(side).length > 0) {
            firstNonEmptySide = side;
            break;
        }
    }
    if (!firstNonEmptySide) return null;

    // Offscreen but laid-out container: paper.js sizes the canvas from offsetWidth/offsetHeight,
    // so a display:none / detached container (0x0) would produce a blank capture.
    const container = document.createElement('div');
    container.style.cssText = `position:absolute;left:-99999px;top:0;width:${PREVIEW_CANVAS_SIZE}px;height:${PREVIEW_CANVAS_SIZE}px;pointer-events:none;`;
    document.body.appendChild(container);

    // isViewer=true → no editing chrome; isConfigurator=true → matches the composer product.
    const customizer = new MPlaza.Customizer(true, false, null, true);
    try {
        customizer.showSideImage = false;
        customizer.appendTo(container);
        customizer.model = model;
        customizer.setDesign(design);

        // setSide is async and frames the design (fitToScreen) once every raster item has loaded.
        const setSidePromise = customizer.setSide(firstNonEmptySide) as unknown as Promise<void>;
        await Promise.race([setSidePromise, new Promise<void>((resolve) => setTimeout(resolve, SET_SIDE_TIMEOUT_MS))]);

        // setSide awaits raster images but not fonts; make sure custom fonts are ready before capturing text.
        if (document.fonts?.ready) await document.fonts.ready;

        return customizer.toDataURL();
    } catch (error) {
        console.error('generateDesignPreviewDataUrl: failed to capture predesign template preview', error);
        return null;
    } finally {
        container.remove();
    }
};

/**
 * Generates a 2D preview for each design and stores it on `design.tempPreviewImageData`,
 * which {@link useSaveDesign} forwards to the backend so the design template `previewUrl`
 * gets populated. Designs with no content on any side are left untouched.
 */
export const applyDesignPreviews = async (designs: MPlaza.Design[], areas: ComposerAreaNode[], model: MPlaza.Model): Promise<void> => {
    for (const design of designs) {
        const previewDataUrl = await generateDesignPreviewDataUrl(design, areas, model);
        if (previewDataUrl) design.set('tempPreviewImageData', previewDataUrl);
    }
};
